import React, { FC, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';

import { Select, Text } from '@components/common';
import { Tooltip } from '@components/Tooltip';

import { isRecordingField } from '../utils';

interface Props {
  questions: ScreenerField[];
  question: ScreenerField;
  skipLogic: SkipLogic[];
  onChange: (questionId: number | null) => void;
}

export const QuestionDefaultRuleSelect: FC<Props> = ({ questions, question: currentQuestion, skipLogic, onChange }) => {
  const [value, setValue] = useState<number | null>(currentQuestion.next_screener_field_id);

  // Get list of question IDs already targeted by skip logic rules
  const usedQuestionIds = useMemo(() => skipLogic.map((sl) => sl.to), [skipLogic]);

  // Build options for questions that come after the current question
  const options = useMemo(() => {
    const items = questions
      .filter((q) => q.id !== currentQuestion.id && q.position > currentQuestion.position)
      .map((q) => {
        let disabled = usedQuestionIds.includes(q.id);
        let label = q.label || '(Untitled question)';
        let tooltip = '';

        // Apply Loom recording constraints
        if (isRecordingField(currentQuestion, questions)) {
          if (!isRecordingField(q, questions)) {
            disabled = true;
            tooltip = 'Cannot skip over the stop recording step';
          }
        } else {
          if (isRecordingField(q, questions)) {
            disabled = true;
            tooltip = 'Cannot skip over the start recording step';
          }
        }

        // Special labels for Loom fields
        if (q.field_type === 'start_loom') {
          label = 'Start recording';
        } else if (q.field_type === 'stop_loom') {
          label = 'End recording';
        }

        return {
          label: `${q.position}. ${label}`,
          value: String(q.id),
          disabled,
          data: { tooltip }
        };
      });

    items.push({
      label: 'End of the survey',
      value: '-1',
      disabled: false,
      data: { tooltip: 'End of the survey' }
    });

    return items;
  }, [questions, currentQuestion, usedQuestionIds]);

  useEffect(() => {
    const hasDefaultNextQuestionSelected = currentQuestion.next_screener_field_id;
    const endOfSurvey = -1;
    if (!hasDefaultNextQuestionSelected) {
      const nextQuestion = questions.find((q) => q.position === currentQuestion.position + 1);
      setValue(nextQuestion?.id || endOfSurvey);
    }
  }, [currentQuestion, questions]);

  useEffect(() => {
    onChange(value);
  }, [value, onChange]);

  return (
    <Select<string>
      options={options}
      placeholder='Select option...'
      className='w-full bg-white'
      dataTestId='default-rule-logic-select'
      value={value ? String(value) : ''}
      renderLabel={({ label, disabled, data }) => (
        <div className={cn({ 'text-gray-500': disabled })}>
          <Tooltip isDisabled={!disabled} content={data.tooltip}>
            <div className='truncate'>
              <Text h='400'>{label}</Text>
            </div>
          </Tooltip>
        </div>
      )}
      onChange={(id) => setValue(id ? Number(id) : null)}
    />
  );
};
